import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, ShieldCheck, ExternalLink, Briefcase, Users, LineChart as LineChartIcon,
  DollarSign, LogOut, Layers, Search, ArrowRight, ChevronDown, ChevronRight,
} from "lucide-react";
import { api } from "../lib/api";
import { useCSuiteAuth } from "../state/CSuiteAuthContext";
import { Navigate, useNavigate } from "react-router-dom";

/**
 * Admin control room.
 *
 * Four sections stacked:
 *   1. Quick-jump cards (My Workspace, Executive Portal, Sign Out)
 *   2. Subscribers table with drill-down (expands to show that user's activity)
 *   3. Payments table
 *   4. Portfolio — every project on the platform, click-through to full workspace
 *
 * Projects don't yet carry an `ownerEmail` — every logged-in admin sees every
 * project. This is intentional for the current single-tenant SAP delivery model.
 * If per-subscriber ownership is added later, filter this table by
 * `subscriber.email` and pass the same `?admin=1` flag through.
 */
export default function AdminSubscribers() {
  const nav = useNavigate();
  const { user, loading: authLoading, logout } = useCSuiteAuth();
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.adminSubscribers().then(setData).catch(e => setErr(e?.response?.data?.detail || e.message));
    api.list().then(setProjects).catch(e => setProjects([]) || setErr(e?.response?.data?.detail || e.message));
  }, []);

  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    if (!q) return projects;
    const qq = q.toLowerCase();
    return projects.filter(p =>
      (p.name || "").toLowerCase().includes(qq) ||
      (p.code || "").toLowerCase().includes(qq) ||
      (p.pm   || "").toLowerCase().includes(qq),
    );
  }, [projects, q]);

  const paymentsByEmail = useMemo(() => {
    const m = {};
    if (!data) return m;
    for (const p of data.payments) {
      const e = (p.user_email || "").toLowerCase();
      if (!e) continue;
      (m[e] ||= []).push(p);
    }
    return m;
  }, [data]);

  if (authLoading) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin" /></div>;
  if (!user) return <Navigate to="/login?next=/admin/subscribers" replace />;
  if (user.role !== "ADMIN") return <div className="p-10 text-center text-red-700" data-testid="admin-forbidden">Admin access required.</div>;

  if (err && !data) return <div className="p-10 text-red-700" data-testid="admin-error">Error: {err}</div>;
  if (!data || !projects) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin" /></div>;

  const active = data.users.filter(u => u.subscription?.status === "active");
  const paid = data.payments.filter(p => p.payment_status === "paid");
  const mrr = active.reduce((s, u) => s + (u.subscription?.plan === "pro" ? 350 : u.subscription?.plan === "starter" ? 200 : 0), 0);
  const arr = mrr * 12;

  return (
    <div className="min-h-screen bg-zinc-50 p-6 md:p-10" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <ShieldCheck className="text-zinc-900" />
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Admin · Control Room</h1>
          <div className="ml-auto flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              Signed in as <span className="text-zinc-800">{user.email}</span>
            </span>
            <button className="btn btn-ghost btn-sm inline-flex items-center gap-1"
                    onClick={() => { logout(); nav("/login"); }} data-testid="btn-admin-signout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        {/* Quick-jump cards */}
        <div className="grid md:grid-cols-3 gap-3 mb-6">
          <button className="card p-4 text-left hover:shadow-md transition-shadow"
                  onClick={() => nav("/projects")} data-testid="admin-goto-workspace">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest">My Workspace</div>
                <div className="font-display text-lg font-extrabold mt-1">Open your projects</div>
                <div className="text-xs text-zinc-500 mt-1">Full operator view · every project, every tab</div>
              </div>
              <Briefcase className="text-cyan-700" size={20} />
            </div>
          </button>
          <button className="card p-4 text-left hover:shadow-md transition-shadow"
                  onClick={() => nav("/csuite")} data-testid="admin-goto-csuite">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest">Executive Portal</div>
                <div className="font-display text-lg font-extrabold mt-1">C-Suite dashboard</div>
                <div className="text-xs text-zinc-500 mt-1">Portfolio KPIs · EBITDA · ROI histograms</div>
              </div>
              <LineChartIcon className="text-emerald-700" size={20} />
            </div>
          </button>
          <a className="card p-4 text-left hover:shadow-md transition-shadow block"
             href="https://dashboard.stripe.com/subscriptions" target="_blank" rel="noreferrer"
             data-testid="admin-goto-stripe">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest">Stripe</div>
                <div className="font-display text-lg font-extrabold mt-1">Manage billing</div>
                <div className="text-xs text-zinc-500 mt-1">Subscription lifecycle · invoices · refunds ↗</div>
              </div>
              <DollarSign className="text-violet-700" size={20} />
            </div>
          </a>
        </div>

        {/* Pilot Analytics tile */}
        <AdminPilotAnalytics />

        {/* Poster A/B Analytics tile */}
        <AdminPosterAnalytics />

        {/* KPI row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Kpi icon={<Users size={14} />}      label="Users"                value={data.users.length} />
          <Kpi icon={<ShieldCheck size={14} />} label="Active subscribers"   value={active.length}     tone="green" />
          <Kpi icon={<Briefcase size={14} />}   label="Projects"             value={projects.length}   tone="cyan" />
          <Kpi icon={<DollarSign size={14} />}  label="MRR"                  value={`$${mrr.toLocaleString()}`} />
          <Kpi icon={<DollarSign size={14} />}  label="ARR (projected)"      value={`$${arr.toLocaleString()}`} tone="green" />
        </div>

        {/* Subscribers table with drill-in */}
        <div className="card p-0 overflow-hidden mb-6">
          <div className="px-4 py-2 font-mono text-[10px] uppercase text-zinc-500 border-b border-[var(--border)] flex items-center gap-2">
            <Users size={12} /> Subscribers <span className="text-zinc-400">· click a row for full payment history</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[var(--border)]">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Plan</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Started</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u, i) => {
                const key = (u.email || "").toLowerCase();
                const isOpen = expanded === key;
                const userPays = paymentsByEmail[key] || [];
                return (
                  <React.Fragment key={key || i}>
                    <tr
                      className="border-b border-[var(--border)] last:border-0 cursor-pointer hover:bg-zinc-50"
                      onClick={() => setExpanded(isOpen ? null : key)}
                      data-testid={`admin-user-row-${i}`}
                    >
                      <td className="px-2 text-zinc-400">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-2 font-medium">{u.email}</td>
                      <td className="px-3 py-2"><span className="pill pill-neutral text-[10px]">{u.role}</span></td>
                      <td className="px-3 py-2">{u.subscription?.plan || "—"}</td>
                      <td className="px-3 py-2">
                        <span className={`pill text-[10px] ${u.subscription?.status === "active" ? "pill-green" : "pill-neutral"}`}>
                          {u.subscription?.status || "no subscription"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-zinc-500">{u.subscription?.startedAt?.slice(0, 10) || "—"}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-zinc-50/60 border-b border-[var(--border)]" data-testid={`admin-user-drill-${i}`}>
                        <td></td>
                        <td colSpan={5} className="px-3 py-3">
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest mb-1">Payments</div>
                              {userPays.length === 0 && <div className="text-xs text-zinc-500">No transactions on record.</div>}
                              {userPays.length > 0 && (
                                <ul className="text-xs space-y-1">
                                  {userPays.slice(0, 20).map((p, j) => (
                                    <li key={j} className="flex items-center justify-between gap-2 py-0.5">
                                      <span className="text-zinc-500 font-mono">{(p.created_at || "").slice(0, 16)}</span>
                                      <span>{p.plan || "—"}</span>
                                      <span className="font-mono">${((p.amount || 0) / 100).toFixed(2)}</span>
                                      <span className={`pill text-[10px] ${p.payment_status === "paid" ? "pill-green" : "pill-neutral"}`}>
                                        {p.payment_status || "—"}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div>
                              <div className="font-mono text-[10px] uppercase text-zinc-500 tracking-widest mb-1">Subscription</div>
                              <div className="text-xs space-y-1">
                                <div><span className="text-zinc-500">Plan:</span> <b>{u.subscription?.plan || "—"}</b></div>
                                <div><span className="text-zinc-500">Status:</span> <b>{u.subscription?.status || "—"}</b></div>
                                <div><span className="text-zinc-500">Started:</span> {u.subscription?.startedAt?.slice(0, 10) || "—"}</div>
                                {u.subscription?.canceledAt && <div><span className="text-zinc-500">Canceled:</span> {u.subscription.canceledAt.slice(0, 10)}</div>}
                                <div><span className="text-zinc-500">Stripe sub:</span> <code className="font-mono text-[10px]">{u.subscription?.stripe_subscription_id || "—"}</code></div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Portfolio — all projects with click-through */}
        <div className="card p-0 overflow-hidden mb-6" data-testid="admin-portfolio-card">
          <div className="px-4 py-2 border-b border-[var(--border)] flex items-center gap-3 flex-wrap">
            <div className="font-mono text-[10px] uppercase text-zinc-500 flex items-center gap-2">
              <Layers size={12} /> Portfolio · {filteredProjects.length} project{filteredProjects.length === 1 ? "" : "s"}
            </div>
            <div className="ml-auto flex items-center gap-2 min-w-[220px]">
              <Search size={14} className="text-zinc-400" />
              <input
                type="text"
                className="fld py-1 px-2 text-xs w-full"
                placeholder="Search by name, code or PM…"
                value={q}
                onChange={e => setQ(e.target.value)}
                data-testid="admin-portfolio-search"
              />
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-3 py-2">Project</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Phase</th>
                <th className="text-left px-3 py-2">Health</th>
                <th className="text-left px-3 py-2">PM</th>
                <th className="text-left px-3 py-2">End</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {filteredProjects.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500 text-sm">No projects match your filter.</td></tr>
              )}
              {filteredProjects.map((p, i) => (
                <tr key={p.id} className="border-b border-[var(--border)] last:border-0 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-medium">{p.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-500">{p.code || "—"}</td>
                  <td className="px-3 py-2 text-xs">{p.phase || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`pill text-[10px] ${healthTone(p.health)}`}>{p.health || "—"}</span>
                  </td>
                  <td className="px-3 py-2 text-xs">{p.pm || "—"}</td>
                  <td className="px-3 py-2 text-xs">{p.end || "—"}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{(p.updatedAt || "").slice(0, 10) || "—"}</td>
                  <td className="px-3 py-2">
                    <button
                      className="btn btn-secondary btn-sm inline-flex items-center gap-1"
                      onClick={() => nav(`/projects/${p.id}`)}
                      data-testid={`admin-open-project-${i}`}
                      title="Open in full workspace"
                    >
                      Open <ArrowRight size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Payments table (full list) */}
        <div className="card p-0 overflow-hidden">
          <div className="px-4 py-2 font-mono text-[10px] uppercase text-zinc-500 border-b border-[var(--border)]">
            Payments · {paid.length} paid of {data.payments.length}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-[var(--border)]">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Plan</th>
                <th className="text-left px-3 py-2">Amount</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Session</th>
              </tr>
            </thead>
            <tbody>
              {data.payments.slice(0, 100).map((p, i) => (
                <tr key={i} className="border-b border-[var(--border)] last:border-0">
                  <td className="px-3 py-2 text-xs">{p.created_at?.slice?.(0, 16) || ""}</td>
                  <td className="px-3 py-2">{p.user_email}</td>
                  <td className="px-3 py-2">{p.plan}</td>
                  <td className="px-3 py-2">${((p.amount || 0) / 100).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <span className={`pill text-[10px] ${p.payment_status === "paid" ? "pill-green" : "pill-neutral"}`}>{p.payment_status}</span>
                  </td>
                  <td className="px-3 py-2">
                    {p.session_id && (
                      <a className="text-xs text-cyan-700 hover:underline inline-flex items-center gap-1 font-mono"
                         href={`https://dashboard.stripe.com/payments/${p.session_id}`} target="_blank" rel="noreferrer">
                        {p.session_id.slice(0, 12)}… <ExternalLink size={10} />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 pt-4 border-t border-[var(--border)] flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 flex-wrap gap-2">
          <div>© Capittance 2026 · Admin Control Room</div>
          <div>Support · <a href="mailto:Requests@Capittance.com?subject=Admin%20support%20request" className="underline hover:no-underline" data-testid="admin-support-link">Requests@Capittance.com</a></div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, tone }) {
  const toneCls = tone === "green" ? "text-green-700" : tone === "cyan" ? "text-cyan-700" : "text-zinc-900";
  return (
    <div className="card p-4">
      <div className="flex items-center gap-1 font-mono text-[10px] uppercase text-zinc-500 tracking-widest">{icon}{label}</div>
      <div className={`font-display text-2xl font-extrabold ${toneCls}`}>{value}</div>
    </div>
  );
}

function healthTone(h) {
  if (h === "On Track") return "pill-green";
  if (h === "At Risk")  return "pill-amber";
  if (h === "Off Track") return "pill-red";
  return "pill-neutral";
}


// ─── Pilot Analytics tile ────────────────────────────────────────────────
function AdminPilotAnalytics() {
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.pilotAnalytics()
      .then(setA)
      .catch(e => setErr(e.message || "Failed to load"));
  }, []);
  if (err) return null;  // silently hide if backend not ready
  if (!a) return <div className="card p-4 mb-6 text-sm text-zinc-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading pilot analytics…</div>;
  const maxCancel = Math.max(1, ...a.cancel_day_histogram.map(x => x.count));
  return (
    <div className="card p-5 mb-6" data-testid="admin-pilot-analytics">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-sm bg-amber-100 grid place-items-center text-amber-700">🎯</div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">14-day Platinum pilot</div>
          <div className="font-display text-lg font-extrabold tracking-tight text-zinc-900">Pilot conversion analytics</div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <PilotStat label="Pilots started"   value={a.totals.pilot_started}   testid="pilot-stat-started"/>
        <PilotStat label="Converted"        value={a.totals.pilot_converted} testid="pilot-stat-converted"/>
        <PilotStat label="Cancelled"        value={a.totals.pilot_cancelled} tone="text-red-700" testid="pilot-stat-cancelled"/>
        <PilotStat label="Conversion rate" value={`${a.conversion_rate_pct}%`} tone={a.conversion_rate_pct >= 50 ? "text-emerald-700" : "text-amber-700"} testid="pilot-stat-rate"/>
      </div>
      <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">Cancel-day histogram (day 1-14)</div>
      <div className="flex items-end gap-1 h-24 mb-1" data-testid="pilot-histogram">
        {a.cancel_day_histogram.map(({ day, count }) => (
          <div key={day} className="flex-1 flex flex-col items-center gap-1" title={`Day ${day} · ${count} cancels`}>
            <div className="w-full rounded-sm" style={{ background: count > 0 ? "#DC2626" : "#F4F4F5", height: `${(count / maxCancel) * 100}%`, minHeight: 2 }} data-testid={`pilot-hist-day-${day}`}/>
            <div className="font-mono text-[9px] text-zinc-500">{day}</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] font-mono text-zinc-400 mb-4">Day since pilot started · red bars indicate cancellations</div>

      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Last 20 pilot events</div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <tbody>
            {a.recent.map((r, i) => (
              <tr key={i} className="border-t border-zinc-100" data-testid={`pilot-event-${i}`}>
                <td className="py-1.5 pr-2 font-mono text-zinc-500 text-[10px]">{(r.at || "").slice(0, 10)}</td>
                <td className="py-1.5 pr-2 font-mono truncate max-w-[260px]">{r.email || "—"}</td>
                <td className="py-1.5">
                  <span className={`pill text-[9px] ${r.event === "pilot_converted" ? "pill-green" : r.event === "pilot_cancelled" ? "pill-red" : r.event.includes("email") ? "pill-blue" : "pill-neutral"}`}>{r.event}</span>
                </td>
              </tr>
            ))}
            {!a.recent.length && <tr><td colSpan={3} className="py-4 text-center text-zinc-400 italic">No pilot events yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function PilotStat({ label, value, tone = "text-zinc-900", testid }) {
  return (
    <div className="border border-[var(--border)] rounded-sm p-3" data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${tone}`}>{value}</div>
    </div>
  );
}

// ─── Poster A/B Analytics tile ───────────────────────────────────────────
const POSTER_LABEL = {
  copilot: "AI Copilot",
  boardpack: "Board Pack",
  pilot: "14-day Pilot",
  cockpits: "Live Cockpits",
  referral: "Referral Rewards",
};
const POSTER_TONE = {
  copilot: "#7A1F2B", boardpack: "#064E60", pilot: "#B45309",
  cockpits: "#0369A1", referral: "#047857",
};
function AdminPosterAnalytics() {
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.posterAnalytics().then(setA).catch(e => setErr(e.message || "")); }, []);
  if (err) return null;
  if (!a) return <div className="card p-4 mb-6 text-sm text-zinc-500 flex items-center gap-1.5"><Loader2 size={12} className="animate-spin"/> Loading poster analytics…</div>;
  const maxVisits = Math.max(1, ...a.rows.map(r => r.visits));
  return (
    <div className="card p-5 mb-6" data-testid="admin-poster-analytics">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-7 h-7 rounded-sm bg-indigo-100 grid place-items-center text-indigo-700">📣</div>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">LinkedIn campaign A/B</div>
          <div className="font-display text-lg font-extrabold tracking-tight text-zinc-900">Poster performance</div>
        </div>
        {a.winner && (
          <div className="ml-auto pill pill-green text-[9px]" data-testid="poster-winner">
            Winner · {POSTER_LABEL[a.winner]}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <PilotStat label="Total visits"   value={a.totals.visits}         testid="poster-total-visits"/>
        <PilotStat label="Total signups"  value={a.totals.signups}        testid="poster-total-signups"/>
        <PilotStat label="Blended rate"   value={`${a.totals.conversion_pct}%`} tone={a.totals.conversion_pct >= 5 ? "text-emerald-700" : "text-amber-700"} testid="poster-total-rate"/>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left font-mono text-[9px] uppercase tracking-widest text-zinc-500 border-b border-zinc-200/60">
              <th className="py-2 pr-2">Poster</th>
              <th className="py-2 pr-2">Visits</th>
              <th className="py-2 pr-2 w-1/2">Visit share</th>
              <th className="py-2 pr-2">Signups</th>
              <th className="py-2">Conv%</th>
            </tr>
          </thead>
          <tbody>
            {a.rows.map(r => (
              <tr key={r.slug} className="border-b border-zinc-100" data-testid={`poster-row-${r.slug}`}>
                <td className="py-2 pr-2">
                  <span className="pill text-[9px]" style={{ background: POSTER_TONE[r.slug] + "22", color: POSTER_TONE[r.slug] }}>{POSTER_LABEL[r.slug]}</span>
                </td>
                <td className="py-2 pr-2 font-mono">{r.visits}</td>
                <td className="py-2 pr-2">
                  <div className="w-full h-2 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full" style={{ background: POSTER_TONE[r.slug], width: `${(r.visits / maxVisits) * 100}%` }}/>
                  </div>
                </td>
                <td className="py-2 pr-2 font-mono">{r.signups}</td>
                <td className="py-2 font-mono font-bold">{r.conversion_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 text-[10px] font-mono text-zinc-400">
        Attribution via ?utm_campaign=li-&lt;slug&gt; on every poster caption. Signups tracked at successful account creation.
      </div>
    </div>
  );
}

